/**
 * Signup — Phase 2 (Executive Redesign, Entry Experience).
 *
 * Presentation migrated to `@/design-system` primitives + `--ds-*` tokens.
 * MATERIAL-SCOPE PRESERVED VERBATIM:
 *   • Register payload shape + `terms_accepted_at` timestamp
 *   • Password strength scoring + validation rules
 *   • Referral-code sourcing (URL then sessionStorage)
 *   • Beta-code debounced validation UX + analytics events
 *   • Test IDs (`signup-*`)
 *   • Compliance acknowledgement wording — the trailing sentence
 *     "…does not guarantee funding, credit approval, score increases, or
 *     removals." is required legal language, rendered byte-for-byte from
 *     the original problem statement.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff, Gift, KeyRound, Loader2, Check, AlertCircle } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { api, formatApiError } from "@/api/client";
import { trackEvent, captureUtmFromUrl, resolveUtm } from "@/lib/analytics";
import AuthShell from "./AuthShell";
import { FormField } from "./Login";
import { Button, Input, Alert } from "@/design-system";

function scorePassword(pw) {
  let score = 0;
  if (pw.length >= 8) score++;
  if (pw.length >= 12) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/\d/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return Math.min(score, 4);
}

const STRENGTH = ["Too short", "Weak", "Fair", "Strong", "Excellent"];
const STRENGTH_TONE = [
  "text-[var(--ds-color-danger)]",
  "text-[var(--ds-color-warning)]",
  "text-[var(--ds-color-warning)]",
  "text-[var(--ds-color-accent)]",
  "text-[var(--ds-color-success)]",
];

export default function Signup() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refFromUrl = (params.get("ref") || "").trim().toUpperCase();
  let refFromStorage = "";
  try {
    refFromStorage = (window.sessionStorage.getItem("iaos_pending_ref") || "").trim().toUpperCase();
  } catch (e) { /* ignore */ }
  const [referralCode] = useState(refFromUrl || refFromStorage);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const codeFromUrl = (params.get("code") || "").trim().toUpperCase();
  const [accessCode, setAccessCode] = useState(codeFromUrl);
  const [codeState, setCodeState] = useState({ status: "idle", message: "", remaining: null });
  const codeTimerRef = useRef(null);

  useEffect(() => {
    captureUtmFromUrl();
    trackEvent("signup_form_view");
  }, []);

  useEffect(() => {
    if (codeTimerRef.current) clearTimeout(codeTimerRef.current);
    const code = accessCode.trim().toUpperCase();
    if (!code) { setCodeState({ status: "idle", message: "", remaining: null }); return; }
    setCodeState((s) => ({ ...s, status: "checking" }));
    codeTimerRef.current = setTimeout(async () => {
      try {
        const { data } = await api.get(`/auth/beta-code-status/${encodeURIComponent(code)}`);
        trackEvent("beta_code_validate", { code, valid: !!data.valid });
        if (data.valid) {
          setCodeState({
            status: "valid",
            message: data.remaining_uses == null
              ? "Code accepted."
              : `Code accepted — ${data.remaining_uses} use${data.remaining_uses === 1 ? "" : "s"} remaining.`,
            remaining: data.remaining_uses,
          });
        } else {
          setCodeState({ status: "invalid", message: data.reason || "Code not recognized.", remaining: null });
        }
      } catch (err) {
        setCodeState({ status: "invalid", message: formatApiError(err), remaining: null });
      }
    }, 400);
    return () => codeTimerRef.current && clearTimeout(codeTimerRef.current);
  }, [accessCode]);

  const strength = useMemo(() => scorePassword(password), [password]);

  function validate() {
    if (!firstName.trim() || !lastName.trim()) return "Please enter your full name.";
    if (!email.trim()) return "Please enter your email address.";
    if (password.length < 8) return "Password must be at least 8 characters.";
    if (!/\d/.test(password)) return "Password must contain at least one number.";
    if (password !== confirm) return "Passwords do not match.";
    if (!accessCode.trim()) return "An early access code is required to sign up.";
    if (codeState.status === "invalid") return codeState.message || "Access code is not valid.";
    if (codeState.status === "checking") return "Validating access code — one moment…";
    if (!termsAccepted) return "Please accept the Terms of Service and compliance acknowledgement to continue.";
    return "";
  }

  async function onSubmit(e) {
    e.preventDefault();
    const v = validate();
    if (v) { setError(v); return; }
    setError("");
    setSubmitting(true);
    trackEvent("signup_form_submit");
    try {
      const utm = resolveUtm();
      await register({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: email.trim().toLowerCase(),
        password,
        referral_code: referralCode || undefined,
        beta_access_code: accessCode.trim().toUpperCase(),
        signup_referrer: (typeof document !== "undefined" ? document.referrer : "") || undefined,
        utm_source: utm.utm_source || undefined,
        utm_medium: utm.utm_medium || undefined,
        utm_campaign: utm.utm_campaign || undefined,
        utm_content: utm.utm_content || undefined,
        terms_accepted_at: new Date().toISOString(),
      });
      trackEvent("signup_success", { has_referral: !!referralCode });
      trackEvent("account_created", { access_code: accessCode.trim().toUpperCase() });
      trackEvent("beta_code_used", { code: accessCode.trim().toUpperCase() });
      try { window.sessionStorage.removeItem("iaos_pending_ref"); } catch (e) { /* ignore */ }
      navigate("/profile", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  }

  const codeBorder =
    codeState.status === "valid"
      ? "border-[var(--ds-color-success)]"
      : codeState.status === "invalid"
      ? "border-[var(--ds-color-danger)]"
      : "border-[var(--ds-color-border-strong)] focus-within:border-[var(--ds-color-accent)]";

  const submitDisabled = submitting || !termsAccepted;

  return (
    <AuthShell
      testId="signup-page"
      title="Create your account."
      subtitle="Takes about 60 seconds. We'll continue with your profile next."
    >
      {referralCode && (
        <div
          data-testid="signup-referral-pill"
          className="mb-6 inline-flex items-center gap-2 px-3 py-1.5 rounded-[var(--ds-radius-md)] text-[11px] font-semibold uppercase tracking-[0.12em] bg-[var(--ds-color-accent-soft)] text-[var(--ds-color-accent-strong)] border border-[var(--ds-color-accent)]"
        >
          <Gift className="w-3.5 h-3.5" aria-hidden />
          Invited by a friend · code <span className="font-mono ml-1">{referralCode}</span>
        </div>
      )}
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormField label="First name" htmlFor="signup-first-name">
            <Input
              id="signup-first-name"
              data-testid="signup-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              autoComplete="given-name"
            />
          </FormField>
          <FormField label="Last name" htmlFor="signup-last-name">
            <Input
              id="signup-last-name"
              data-testid="signup-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              autoComplete="family-name"
            />
          </FormField>
        </div>
        <FormField label="Email" htmlFor="signup-email">
          <Input
            id="signup-email"
            data-testid="signup-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </FormField>
        <FormField label="Password" htmlFor="signup-password">
          <div className="relative">
            <Input
              id="signup-password"
              data-testid="signup-password"
              type={show ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              className="pr-11"
            />
            <button
              type="button"
              onClick={() => setShow((s) => !s)}
              data-testid="signup-toggle-password"
              aria-label={show ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-2 flex items-center justify-center px-2 text-[var(--ds-color-ink-muted)] hover:text-[var(--ds-color-ink)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ds-color-focus)] rounded-[var(--ds-radius-sm)]"
            >
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </FormField>
        {password.length > 0 && (
          <div className="space-y-2" data-testid="signup-password-strength">
            <div className="flex gap-1">
              {[0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="flex-1 h-1 rounded-[var(--ds-radius-pill)] transition-colors"
                  style={{
                    background: i < strength
                      ? "var(--ds-color-accent)"
                      : "var(--ds-color-surface-inset)",
                  }}
                />
              ))}
            </div>
            <div className="text-[13px] text-[var(--ds-color-ink-muted)]">
              Strength: <span className={STRENGTH_TONE[strength]}>{STRENGTH[strength]}</span>
              {" — "}minimum 8 characters and at least one number.
            </div>
          </div>
        )}
        <FormField label="Confirm password" htmlFor="signup-password-confirm">
          <Input
            id="signup-password-confirm"
            data-testid="signup-password-confirm"
            type={show ? "text" : "password"}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            autoComplete="new-password"
          />
        </FormField>

        {/* Early access code — bespoke composite (keyround icon + status
            indicator). Kept as an inline field rather than a plain Input
            because the trailing status icon changes per validation state. */}
        <div>
          <span className="block text-[11px] font-semibold uppercase tracking-[0.12em] text-[var(--ds-color-ink-muted)] mb-1.5">
            Early access code
          </span>
          <div
            className={`flex items-center gap-2 px-3 h-10 rounded-[var(--ds-radius-md)] bg-[var(--ds-color-surface)] border transition-colors min-w-0 ${codeBorder}`}
          >
            <KeyRound className="w-4 h-4 shrink-0 text-[var(--ds-color-ink-muted)]" aria-hidden />
            <input
              data-testid="signup-beta-code"
              type="text"
              autoComplete="off"
              spellCheck={false}
              value={accessCode}
              onChange={(e) => setAccessCode(e.target.value.toUpperCase())}
              placeholder="INFORMED-2026"
              className="flex-1 min-w-0 w-full bg-transparent outline-none text-[14px] font-mono uppercase tracking-wider text-[var(--ds-color-ink)] placeholder:text-[var(--ds-color-ink-subtle)]"
            />
            {codeState.status === "checking" && (
              <Loader2 className="w-4 h-4 animate-spin shrink-0 text-[var(--ds-color-ink-muted)]" data-testid="signup-beta-checking" aria-hidden />
            )}
            {codeState.status === "valid" && (
              <Check className="w-4 h-4 shrink-0 text-[var(--ds-color-success)]" data-testid="signup-beta-valid" aria-hidden />
            )}
            {codeState.status === "invalid" && (
              <AlertCircle className="w-4 h-4 shrink-0 text-[var(--ds-color-danger)]" data-testid="signup-beta-invalid" aria-hidden />
            )}
          </div>
          {codeState.message ? (
            <div
              data-testid="signup-beta-message"
              className={`text-[13px] mt-1.5 ${
                codeState.status === "valid" ? "text-[var(--ds-color-success)]"
                : codeState.status === "invalid" ? "text-[var(--ds-color-danger)]"
                : "text-[var(--ds-color-ink-muted)]"
              }`}
            >
              {codeState.message}
            </div>
          ) : (
            <div className="text-[13px] text-[var(--ds-color-ink-muted)] mt-1.5">
              We're rolling out access in cohorts. Your code unlocks the platform.
            </div>
          )}
        </div>

        {/* Terms + compliance acknowledgement. Copy is REQUIRED legal
            language — do NOT paraphrase. The trailing sentence is the
            byte-exact wording from the original problem statement. */}
        <label
          htmlFor="signup-terms-checkbox"
          data-testid="signup-terms-row"
          className="flex items-start gap-3 rounded-[var(--ds-radius-md)] border border-[var(--ds-color-border)] bg-[var(--ds-color-surface-muted)] p-3 cursor-pointer hover:border-[var(--ds-color-border-strong)] transition-colors"
        >
          <input
            id="signup-terms-checkbox"
            data-testid="signup-terms-checkbox"
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className="mt-1 shrink-0 h-4 w-4 rounded-[var(--ds-radius-sm)] cursor-pointer"
            style={{ accentColor: "var(--ds-color-accent)" }}
          />
          <span className="text-[13px] leading-relaxed text-[var(--ds-color-ink-muted)]">
            I agree to the{" "}
            <Link
              to="/legal/terms"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)]"
              data-testid="signup-terms-link"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/legal/privacy"
              target="_blank"
              rel="noreferrer"
              className="font-medium text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)]"
              data-testid="signup-privacy-link"
            >
              Privacy Policy
            </Link>
            , and I understand this app provides educational analysis and does not guarantee
            funding, credit approval, score increases, or removals.
          </span>
        </label>

        {error && (
          <Alert tone="danger" data-testid="signup-form-error">
            {error}
          </Alert>
        )}
        <Button
          type="submit"
          data-testid="signup-form-submit"
          loading={submitting}
          disabled={submitDisabled}
          className="w-full"
          size="lg"
        >
          {submitting && <Loader2 className="w-4 h-4 animate-spin" aria-hidden />}
          {submitting ? "Creating account…" : "Create account"}
        </Button>
        <div className="text-[13px] text-center pt-1 text-[var(--ds-color-ink-muted)]">
          Already a member?{" "}
          <Link to="/login" data-testid="signup-to-login-link" className="font-medium text-[var(--ds-color-accent)] hover:text-[var(--ds-color-accent-strong)] transition-colors">
            Sign in
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
